/**
 * @file SkuPickerDialog.tsx
 * @description Боковая панель выбора SKU: поиск по названию/коду, чекбоксы,
 * кнопка подтверждения внизу; выбранное уходит в `onSelectMany` (phase10).
 *
 * Режим `single` (phase19.24 шаг 5, OQ-54) — привязка позиции заказа к товару
 * каталога: выбор замещающий, и кнопка зовёт выбрать ровно один товар. В этом
 * режиме чекбоксов нет — выбирается строка (radio-семантика): галочка рядом с
 * тремя товарами обещала множественный выбор, которого нет.
 */

import { Check, Loader2, Package, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { formatMoney, listSkusForPicker, type OrderSkuDto } from "../../lib/api-orders";
import { formatSalePackage } from "../../lib/qty-input";

interface Props {
  onClose: () => void;
  onSelectMany: (skus: OrderSkuDto[]) => void;
  /**
   * Одиночный выбор: привязка позиции заказа к товару (OQ-54). Выбор
   * замещающий, а не накопительный — иначе менеджер отметил бы три товара, а
   * привязался бы первый.
   */
  single?: boolean;
}

export function SkuPickerDialog({ onClose, onSelectMany, single = false }: Props) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<OrderSkuDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Map<string, OrderSkuDto>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await listSkusForPicker({
          search: search.trim() || undefined,
          limit: 200,
        });
        if (!cancelled) setItems(res);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  function toggle(sku: OrderSkuDto) {
    if (single) {
      setSelected((prev) => (prev.has(sku.id) ? new Map() : new Map([[sku.id, sku]])));
      return;
    }
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(sku.id)) next.delete(sku.id);
      else next.set(sku.id, sku);
      return next;
    });
  }

  function confirm() {
    onSelectMany(Array.from(selected.values()));
  }

  const selectedCount = selected.size;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-on-surface/8">
          <SheetTitle>Каталог · Выбор товар{single ? "а" : "ов"}</SheetTitle>
        </SheetHeader>

        <div className="px-6 py-3 border-b border-on-surface/8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-on-surface-variant" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Название товара или код SKU"
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-6 animate-spin text-on-surface-variant" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-on-surface-variant text-sm">
              Товаров не найдено
            </div>
          ) : (
            <ul
              className="flex flex-col gap-1"
              // Одиночный выбор — это переключатели, а не галочки: чекбоксы
              // обещали «можно отметить несколько», хотя выбор замещающий
              // (дефект QA 2026-09-21).
              role={single ? "radiogroup" : undefined}
              aria-label={single ? "Товары каталога" : undefined}
            >
              {items.map((s) => {
                const isSel = selected.has(s.id);
                return (
                  <li
                    key={s.id}
                    className={`flex min-h-12 items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      isSel
                        ? "bg-primary/10 [box-shadow:inset_0_0_0_1px_var(--primary)]"
                        : "hover:bg-surface/40"
                    }`}
                    onClick={() => toggle(s)}
                    role={single ? "radio" : "button"}
                    aria-checked={single ? isSel : undefined}
                    aria-label={single ? s.productName : undefined}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(s);
                      }
                    }}
                  >
                    {single ? (
                      // Подсвеченная строка + галочка: отдельного контрола нет,
                      // выбирается сама строка.
                      <Check
                        className={`size-4 shrink-0 ${isSel ? "text-primary" : "opacity-0"}`}
                        aria-hidden
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(s)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-primary size-4 shrink-0"
                        aria-label={`Выбрать ${s.productName}`}
                      />
                    )}
                    <div className="size-8 rounded bg-surface flex items-center justify-center shrink-0">
                      <Package className="size-4 text-on-surface-variant" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">
                          {s.productName}
                        </span>
                        <Badge variant="outline" className="text-[10px] font-mono">
                          {s.code}
                        </Badge>
                        {s.outOfStock ? (
                          <Badge variant="destructive" className="text-[10px]">
                            нет
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-on-surface-variant truncate">
                        {/* Фасовка через реестр единиц: «50 г», не «50.000 g» (TD-53). */}
                        {formatSalePackage(s.saleQuantity, s.saleUnit)}
                        {s.gtin ? ` · GTIN ${s.gtin}` : ""}
                        {` · НДС ${s.vatRate}%`}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-primary">
                        {formatMoney(s.priceGross)} ₽
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <SheetFooter className="border-t border-on-surface/8 px-6 py-3 flex flex-row items-center justify-between gap-2">
          <span className="text-sm text-on-surface-variant">
            Выбрано: <b className="text-on-surface">{selectedCount}</b>
          </span>
          <div className="flex gap-2">
            {/* Тач-таргеты ≥48px: панель открывают и с телефона (phase19.15). */}
            <Button variant="outline" className="min-h-12" onClick={onClose} size="sm">
              Отмена
            </Button>
            <Button
              className="min-h-12"
              onClick={confirm}
              disabled={selectedCount === 0}
              size="sm"
            >
              {single ? "Выбрать товар" : `Добавить ${selectedCount > 0 ? `(${selectedCount})` : ""}`}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
